import { useEffect, useState, useRef } from 'react'
import {
  ApiError, startLive, stopLive, type Calibration, type LiveInfo,
} from '../api'
import { useApi } from '../useApi'
import { takeListenIntent } from '../listenIntent'
import { useLiveAudio } from '../live'
import { LiveSpectrogram, SpecLegend } from './Spectrogram'
import { Ridgeline } from './Ridgeline'
import { LevelMeter } from './LevelMeter'

interface LiveStatusDoc {
  segments: number
  newest: string | null
  live_service_desired: string
  window_seconds: number
}

/** Live listening as one action.
 *
 *  There is no command channel and none is needed: enabling live means writing
 *  `services.live = "running"` into desired state and waiting for the node's
 *  reconciler to notice. That takes a poll interval, so the button owns the
 *  whole sequence -- ask, wait for the first segment, then play -- rather than
 *  making the operator understand the control plane to hear the sea.
 */
export function ListenPanel({
  missionId, computeId, calibration, canWrite,
}: {
  missionId: string
  computeId: string | null
  calibration: Calibration
  canWrite: boolean
}) {
  const [arming, setArming] = useState(false)
  const [armError, setArmError] = useState<string | null>(null)
  const [armedAt, setArmedAt] = useState(0)
  const [waited, setWaited] = useState(0)
  // Which sensor on the node: '' is the hydrophone, 'moth' a HydroMoth
  // beside it. One stream runs at a time; the server flips them together.
  const [stream, setStream] = useState('')
  useEffect(() => { setStream('') }, [missionId])
  const streamsInfo = useApi<LiveInfo>(
    `/v1/missions/${encodeURIComponent(missionId)}/live?window=5`)
  // The camera is not a sound: it is watched from the node page.
  const streams = (streamsInfo.data?.streams ?? []).filter((s) => s.kind !== 'camera')
  const audio = useLiveAudio(missionId, stream)
  // WebGL2 is nearly universal now, but a lost context or an old browser must
  // not cost someone the picture entirely, so the 2D waterfall stays as the
  // fallback rather than as a second thing to maintain by choice.
  const [flat, setFlat] = useState(false)
  // What the live view is currently drawing, so the legend states it rather
  // than printing a constant that stopped being true.
  const [range, setRange] = useState<[number, number]>([-100, -40])

  /* Did we get here by someone pressing Listen?
   *
   * Read once, on mount, and consumed in the reading -- so a reload or a back
   * button lands on a panel that waits, which is right. Only a press means
   * "start".
   */
  const [autoListen, setAutoListen] = useState(takeListenIntent)
  const autoStarted = useRef(false)

  // Poll only while we are waiting for the node to converge, or while playing --
  // and from the outset when we already intend to listen, because the decision
  // below has to be made against what the node is actually doing.
  const watching = arming || autoListen || audio.status !== 'idle'
  const status = useApi<LiveStatusDoc>(
    watching
      ? `/v1/missions/${encodeURIComponent(missionId)}/live?window=45` +
        (stream ? `&stream=${encodeURIComponent(stream)}` : '')
      : null,
    { poll: 3000 },
  )

  const desired = status.data?.live_service_desired
  const segments = status.data?.segments ?? 0

  // The moment the first segment lands, start playing. Waiting for a second
  // click here would be the difference between "it works" and "it works if you
  // know to press it again".
  //
  // Depends on `start` and `status`, not on the whole `audio` object: the hook
  // returns a fresh object every render, so depending on it ran this effect on
  // every render and made a second `start()` a matter of timing. Two overlapping
  // starts are what produced "playback blocked: AbortError".
  const { start: startAudio, status: audioStatus } = audio
  useEffect(() => {
    if (arming && segments > 0 && audioStatus === 'idle') {
      setArming(false)
      startAudio()
    }
  }, [arming, segments, audioStatus, startAudio])

  // Waiting is the normal case, not the exception: there is no command channel,
  // so enabling live means writing desired state and waiting for the node to
  // poll. That can be a couple of minutes. A panel that says nothing for two
  // minutes reads as broken, so it counts out loud.
  useEffect(() => {
    if (!arming) { setWaited(0); return }
    const t = window.setInterval(
      () => setWaited(Math.floor((Date.now() - armedAt) / 1000)), 500)
    return () => window.clearInterval(t)
  }, [arming, armedAt])

  const listen = async () => {
    // Pressing it again while a start is already in flight tears down the
    // stream that was about to play and reports the teardown as an error.
    if (arming || audio.status !== 'idle') return
    setArmError(null)
    if (segments > 0) {
      audio.start()
      return
    }
    if (!computeId) {
      setArmError('This mission has no compute bound to it yet.')
      return
    }
    if (!canWrite) {
      setArmError('Live is not running on this node, and your role cannot enable it.')
      return
    }
    setArming(true)
    setArmedAt(Date.now())
    setWaited(0)
    try {
      await startLive(missionId, stream)
      void status.reload()
    } catch (e) {
      setArming(false)
      setArmError(
        e instanceof ApiError
          ? e.detail ?? e.code
          : 'Could not write desired state.',
      )
    }
  }

  /* Press Listen on the fleet, get sound. No second press.
   *
   * Held until the first status answer arrives rather than firing on mount:
   * `listen()` branches on `segments`, which is 0 before anything has been
   * fetched. Acting on that would take the arm-the-service path even on a node
   * already streaming -- writing desired state nobody needed and adding a poll
   * interval to something that could have played at once.
   *
   * `listen()` keeps every guard it has. A viewer who cannot enable live gets
   * the same explanation they would have got by pressing the button, which is
   * the point: this removes a press, not a check.
   */
  useEffect(() => {
    if (!autoListen || autoStarted.current || status.data === undefined) return
    autoStarted.current = true
    setAutoListen(false)
    void listen()
    // `listen` is rebuilt every render and is deliberately absent: the ref
    // guard is what makes this run once, and depending on it would re-run the
    // effect on every render instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoListen, status.data])

  /* Switching sensor while listening keeps listening: start the other stream
   * (which stops this one in the same revision) and play it on arrival. */
  const pickStream = async (next: string) => {
    if (next === stream) return
    const wasOn = arming || audio.status !== 'idle'
    audio.stop()
    setStream(next)
    if (!wasOn || !canWrite) { setArming(false); return }
    setArmError(null)
    setArming(true)
    setArmedAt(Date.now())
    setWaited(0)
    try {
      await startLive(missionId, next)
    } catch (e) {
      setArming(false)
      setArmError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    }
  }

  const stopAll = async () => {
    audio.stop()
    setArming(false)
    // Leaving the service running would keep the node uploading 1-second
    // segments to a bucket nobody is listening to. R7.1: on demand only.
    if (computeId && canWrite && desired === 'running') {
      try {
        await stopLive(missionId)
        void status.reload()
      } catch {
        /* The audio has already stopped; a failed cleanup is worth a retry
           later, not an error in the operator's face. */
      }
    }
  }

  const playing = audio.status === 'playing' || audio.status === 'stalled'
  const starting = audio.status === 'starting'
  const busy = arming || starting

  return (
    <div className="card flush">
      <div className="card-head">
        <h2>Live</h2>
        {streams.length > 1 && (
          <div className="seg sm" role="tablist" aria-label="Sensor">
            {streams.map((s) => (
              <button key={s.id} type="button" role="tab"
                      aria-selected={s.id === stream}
                      disabled={s.present === false}
                      title={s.present === false ? 'Not connected' : s.live_service}
                      onClick={() => { void pickStream(s.id) }}>
                {s.primary ? 'Hydrophone' : (s.sensor ?? s.id)}
              </button>
            ))}
          </div>
        )}
        {playing && (
          <span className="badge alert">
            <span className="dot-live" /> on air
          </span>
        )}
        {audio.latency !== null && (
          <span className="faint num" style={{ fontSize: 13 }}>
            {audio.latency.toFixed(1)} s behind
          </span>
        )}
        {audio.recording && (
          <span className="badge alert" role="status">
            <span className="dot-live" />
            REC {Math.floor(audio.recordedSeconds / 60)}:
            {String(audio.recordedSeconds % 60).padStart(2, '0')}
          </span>
        )}
        <div className="cta">
          {playing && (
            // Dashcam: the reason to press this is almost always something you
            // have just heard, so it saves straight to the device rather than
            // asking where to put it or routing bytes through the server.
            <button className={`btn${audio.recording ? ' danger' : ''}`}
              onClick={() => audio.recording ? audio.stopRecording() : audio.startRecording()}>
              {audio.recording ? 'Stop and save' : 'Record'}
            </button>
          )}
          {playing || busy ? (
            <button className="btn" onClick={() => void stopAll()}>
              Stop
            </button>
          ) : (
            <button className="btn primary" onClick={() => void listen()}>
              Listen live
            </button>
          )}
        </div>
      </div>

      <div className="card-body stack">
        {armError && <div className="notice bad">{armError}</div>}
        {audio.error && <div className="notice bad">{audio.error}</div>}

        {arming && (
          <div className="notice">
            <strong>Waiting for the node.</strong>{' '}
            {desired === 'running'
              ? 'It has been asked to stream and picks that up the next time it checks in.'
              : 'Writing the request now.'}{' '}
            Observed on this fleet: <span className="num">30 s to 3 min</span>,
            because there is no command channel — the node polls, and nothing
            here can hurry it.
            <div className="faint num" style={{ marginTop: 6 }}>
              {waited}s elapsed · {segments} segment{segments === 1 ? '' : 's'} so far
              {waited > 210 && (
                <>
                  {' '}· longer than expected. The node may be offline; Activity
                  shows when it last sent anything.
                </>
              )}
            </div>
          </div>
        )}

        {starting && (
          <div className="notice">
            Segments are arriving. Loading the stream…
          </div>
        )}

        {audio.analyser ? (
          <>
            {flat ? (
              <LiveSpectrogram analyser={audio.analyser} height={300} />
            ) : (
              <Ridgeline
                analyser={audio.analyser}
                height={300}
                onUnsupported={() => setFlat(true)}
                onRange={(lo, hi) => setRange([lo, hi])}
              />
            )}
            <LevelMeter analyser={audio.analyser} />
            <SpecLegend
              basis={calibration.basis}
              absolute={calibration.absolute}
              sampleRate={audio.analyser.context.sampleRate}
              minDb={flat ? undefined : range[0]}
              maxDb={flat ? undefined : range[1]}
              note={flat ? undefined : 'scale follows the signal'}
            />
            {/* Where the last recording got to. Stated separately from the
                download, because they succeed and fail independently: the file
                is on the device either way, and on a farm uplink the upload is
                the half most likely not to work. */}
            {!audio.recording && audio.clipState !== 'idle' && (
              <div className={`notice${audio.clipState === 'failed' ? ' bad' : ''}`}>
                {audio.clipState === 'uploading' && 'Saving the clip to the archive…'}
                {audio.clipState === 'saved' && (
                  <>
                    <strong>Clip saved.</strong> It is in the archive against
                    this mission, and on this device as a file.
                    {audio.clipLimitedDb > 1 && (
                      <div className="faint num" style={{ marginTop: 6 }}>
                        Something louder than the rest of the session arrived
                        during it: the limiter held back{' '}
                        {audio.clipLimitedDb.toFixed(1)} dB at the peak, so those
                        moments are not a linear record of the level.
                      </div>
                    )}
                  </>
                )}
                {audio.clipState === 'failed' && (
                  <>
                    <strong>Saved to this device, but not to the archive.</strong>{' '}
                    {audio.clipError} The recording is not lost — it downloaded
                    when you pressed stop.
                  </>
                )}
              </div>
            )}

            {audio.recording && (
              <div className="notice">
                Recording to this device, and to the archive when you stop.
                {/* The capture gain is stated because it is what makes the file
                    interpretable later: it is chosen by measuring the signal
                    when Record is pressed, held fixed for the whole recording,
                    and written into the filename. The monitor slider does not
                    affect it -- turning the volume down to listen discreetly
                    cannot change what is saved. */}
                <div className="faint num" style={{ marginTop: 6 }}>
                  captured at {audio.recordGainDb >= 0 ? '+' : '−'}
                  {Math.abs(Math.round(audio.recordGainDb))} dB · subtract this
                  to read true level · monitor gain not included
                </div>
              </div>
            )}
            {/* Gain in dB, not a 0-1 volume. An uncalibrated hydrophone in air
                runs 50-80 dB below full scale, so unity is inaudible and reads
                as a fault; the number says how much is being added, and a
                limiter after it keeps a transient from arriving at full boost
                in somebody's headphones. It sits after the analyser, so none of
                this changes the level being reported or recorded. */}
            <div className="row">
              <span className="label">Monitor gain</span>
              <input
                type="range" min={-20} max={40} step={1}
                value={audio.gainDb}
                aria-label="Monitor gain in decibels"
                style={{ width: 180 }}
                onChange={(e) => audio.setGainDb(Number(e.target.value))}
              />
              <span className="num" style={{ minWidth: 62 }}>
                {audio.gainDb > 0 ? '+' : ''}{audio.gainDb} dB
              </span>
            </div>
          </>
        ) : (
          <div className="empty">
            <h3>Nothing playing</h3>
            <p>
              Live audio is on demand. Starting it asks the node to upload
              one-second segments; they expire within the day and never enter
              the archive.
            </p>
            {watching && (
              <p className="faint num">
                {segments} segment{segments === 1 ? '' : 's'} in the last 45 s
                {desired ? ` · service is “${desired}”` : ''}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
